import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatValidUntil, type ParticipantCriteria, type ParticipantDto } from '@app/shared';
import { isForever } from '../../lib/purchase';
import { PageHeader } from '../../components/PageHeader';
import { Badge, Button, Card, Spinner, Toast } from '../../components/ui';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { creditsCount, formatCents, formatDate, parseSettingValue, personLabel } from '../../lib/format';
import { haptic } from '../../lib/tma';

const inputClass =
  'w-full rounded-lg px-4 py-2.5 text-sm bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70';

function Criteria({ criteria }: { criteria: ParticipantCriteria }) {
  const t = useT();
  const { returns, reelCost } = criteria;
  return (
    <Card variant="surface" padding="md" className="mb-4 space-y-2 text-sm">
      <div className="text-xs uppercase opacity-60">{t.admin.criteriaTitle}</div>
      <div>
        <div className="font-medium">{t.admin.returnsLine(returns.withTwo, returns.people, returns.days)}</div>
        {returns.stillEarly > 0 && <div className="text-hint">{t.admin.returnsEarly(returns.stillEarly)}</div>}
        <div className="text-xs text-hint">{t.admin.returnsGoal}</div>
      </div>
      <div>
        {reelCost.results === 0 || reelCost.avgUsd === null || reelCost.maxUsd === null ? (
          <div className="font-medium">{t.admin.reelCostNone}</div>
        ) : (
          <>
            <div className="font-medium">
              {t.admin.reelCostLine(formatCents(reelCost.avgUsd), formatCents(reelCost.maxUsd), reelCost.results)}
            </div>
            {reelCost.maxRebuiltUsd !== null && (
              <div className="text-hint">{t.admin.reelCostRebuilt(formatCents(reelCost.maxRebuiltUsd), reelCost.rebuilt)}</div>
            )}
          </>
        )}
        <div className="text-xs text-hint">{t.admin.reelCostGoal}</div>
      </div>
    </Card>
  );
}

function AddCredits({ person, defaultDays }: { person: ParticipantDto; defaultDays: number }) {
  const t = useT();
  const qc = useQueryClient();
  const [credits, setCredits] = useState('100');
  const [days, setDays] = useState(String(defaultDays));
  const [toast, setToast] = useState<string | null>(null);
  // A double tap lands before the re-render that disables the button: one grant per tap pair.
  const sending = useRef(false);

  const flash = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3500);
  };

  const add = useMutation({
    mutationFn: (body: { credits: number; days: number }) => api.adminAddCredits(person.userId, body),
    onSuccess: (res) => {
      haptic('success');
      flash(res.notified ? t.admin.creditsAddedNotified : t.admin.creditsAddedNotNotified);
    },
    onError: () => flash(t.common.failed),
    onSettled: () => {
      sending.current = false;
      return qc.invalidateQueries({ queryKey: ['admin', 'participants'] });
    },
  });

  const amount = parseSettingValue(credits);
  const life = parseSettingValue(days);
  const valid = amount !== null && amount >= 1 && amount <= 100_000 && life !== null && life >= 1 && life <= 365;

  return (
    <div className="mt-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-hint">
          {t.admin.addCreditsAmount}
          <input inputMode="numeric" value={credits} onChange={(e) => setCredits(e.target.value)} className={`${inputClass} mt-1`} />
        </label>
        <label className="block text-xs text-hint">
          {t.admin.addCreditsDays}
          <input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} className={`${inputClass} mt-1`} />
        </label>
      </div>
      {!valid && <p className="text-xs text-destructive">{t.admin.addCreditsBad}</p>}
      <Button
        variant="ink"
        size="sm"
        fullWidth
        disabled={!valid || add.isPending}
        loading={add.isPending}
        onClick={() => {
          if (amount === null || life === null || sending.current) return;
          sending.current = true;
          add.mutate({ credits: amount, days: life });
        }}
      >
        {t.admin.addCreditsSubmit}
      </Button>
      {toast && (
        <Toast tone="neutral" placement="fixed">
          {toast}
        </Toast>
      )}
    </div>
  );
}

function Participant({ person, defaultDays }: { person: ParticipantDto; defaultDays: number }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <Card variant="surface" padding="md">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold break-all">{personLabel(person)}</span>
        {person.isAdmin && <Badge tone="neutral">{t.admin.participantAdmin}</Badge>}
      </div>
      <p className="text-xs text-hint mt-0.5">
        {t.admin.participantSince(formatDate(person.accessGrantedAt))} ·{' '}
        {person.lastActiveAt ? t.admin.participantSeen(formatDate(person.lastActiveAt)) : t.admin.participantIdle}
      </p>
      {person.startSource !== null && (
        <p className="text-xs text-hint mt-0.5 break-all">{t.admin.participantSource(person.startSource)}</p>
      )}
      <div className="mt-2 space-y-0.5 text-sm">
        <div>
          {t.admin.participantBalance(creditsCount(person.balance))}
          {person.nextExpiry && !isForever(person.nextExpiry.expiresAt) && (
            <span className="text-hint">
              {' '}
              · {t.admin.participantBurns(creditsCount(person.nextExpiry.credits), formatValidUntil(person.nextExpiry.expiresAt))}
            </span>
          )}
        </div>
        <div>{t.admin.participantMade(person.thoughts, person.results)}</div>
        <div>{t.admin.participantRatings(person.likes, person.dislikes)}</div>
        <div className="text-hint">{t.admin.participantCost(formatCents(person.costUsd))}</div>
      </div>
      {open ? (
        <AddCredits person={person} defaultDays={defaultDays} />
      ) : (
        <Button variant="ink" size="sm" className="mt-3" onClick={() => setOpen(true)}>
          {t.admin.addCredits}
        </Button>
      )}
    </Card>
  );
}

export function AdminParticipantsPage() {
  const t = useT();
  const list = useQuery({
    queryKey: ['admin', 'participants'],
    queryFn: () => api.adminListParticipants(),
  });

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.admin.participants} to="/admin" />

      {list.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {list.isError && <p className="text-sm text-hint">{t.common.failed}</p>}

      {list.data && (
        <>
          <Criteria criteria={list.data.criteria} />
          {list.data.participants.length === 0 && <p className="text-sm text-hint">{t.admin.participantsEmpty}</p>}
          <div className="space-y-2">
            {list.data.participants.map((p) => (
              <Participant key={p.userId} person={p} defaultDays={list.data.creditDays} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
